import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { kycDocumentsService } from '../../../services/kycDocuments.service';
import { setActiveInvestorJourney, getActiveInvestorJourney } from '../../../services/investorJourneyScope';
import { profileService,
  PropagationOffer, InvestorConsentsDto, InvestorExperienceDto, UserPersonalInformationDto, UserPassportDetailsDto, UserResidentialStatusDto, UserTaxInfoDto, UserBankDetailsDto, UserContactDetailsDto, UserNominationDto, UserRiskProfileDto } from '../../../services/profile.service';
import { investorService, InvestorDashboardDto } from '../../../services/investor.service';
import { contentService, type MasterCountryDto, type IsdCodeValuesDto, MasterLookupDto } from '../../../services/content.service';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import { useSAProxyNavigation } from '../../../hooks/useSAProxyNavigation';
import { useDelegationPermissions } from '../../../contexts/DelegationPermissionsContext';
import { editRequestService, EditAccessDto } from '../../../services/editRequest.service';
import { toast } from 'react-toastify';
import { normalizeDateForInput, normalizeCountryOfResidenceForForm } from '../../../utils/formHelpers';
import { SharedFormContext, TabType } from './shared/types';
import { PropagationPrompt } from './shared/PropagationPrompt';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';
import { PersonalInformationForm } from './PersonalInformation/PersonalInformationForm';
import { BankDetailsForm } from './BankDetails/BankDetailsForm';
import { TaxInformationForm } from './TaxInformation/TaxInformationForm';
import { ContactDetailsForm } from './ContactDetails/ContactDetailsForm';
import { NominationForm } from './Nomination/NominationForm';
import { PassportDetailsForm } from './PassportDetails/PassportDetailsForm';
import { ResidentialStatusForm } from './ResidentialStatus/ResidentialStatusForm';
import { RiskProfileForm } from './RiskProfile/RiskProfileForm';
import { FinalSubmissionForm } from './FinalSubmission/FinalSubmissionForm';
import { PremiumJourneyStepper } from '../../../components/PremiumJourneyStepper/PremiumJourneyStepper';
import { isSectionVisible, getSectionLabel, type ProfileSectionKey } from '../../../config/profileVisibility';
import { scopeCoversSection, SCOPE_LABELS, type InfoSection } from '../../../utils/delegationScope';
import type { DelegationScope } from '../../../models/DelegationDto';
import '../InvestorProfile/InvestorProfile.scss';

/**
 * Container component for investor information forms
 * Coordinates tab navigation, shared state, and data loading
 * Replaces the monolithic InvestorProfile.tsx
 */
export const InformationContainer: React.FC = () => {
  const navigate = useNavigate();
  const { journeyId: routeJourneyId } = useParams<{ journeyId?: string }>();
  // Multi-journey scope (user 2026-08-25): a bare /investor/journey (stepper's
  // Information tab, old links) used to CLEAR the active scope and load the primary
  // journey — silently tearing a multi-journey investor out of the journey they were
  // working in. Adopt the active scope instead; clearing only happens when there is
  // genuinely no journey context at all.
  const journeyId = routeJourneyId ?? getActiveInvestorJourney() ?? undefined;
  const { navigate: saNavigate, isProxyMode } = useSAProxyNavigation();
  const delegationPerms = useDelegationPermissions();

  // Per section (2026-10-05): the agent needs the edit flag AND a scope that reaches the tab's
  // group — CKYC Only stops at the KYC tabs. The backend enforces the same rule.
  const canEditSection = (section: InfoSection) =>
    !delegationPerms.isProxyMode
    || (delegationPerms.canEditKyc && scopeCoversSection(delegationPerms.scope, section));
  const canSubmit = !delegationPerms.isProxyMode || delegationPerms.canSubmitForms;

  // ── Re-edit lock (post-final-submission) ──────────────────────────────────
  // Purely additive to the happy flow: `reEditLocked` can only be true when the BACKEND
  // enforcement flag is on (editAccess.enforced) — while it is off, editing behaves exactly
  // as before and this state only powers the informational banner / request button.
  const [editAccess, setEditAccess] = useState<EditAccessDto | null>(null);
  const [showRaiseModal, setShowRaiseModal] = useState(false);
  const [raiseReason, setRaiseReason] = useState('');
  const [raising, setRaising] = useState(false);

  const refreshEditAccess = React.useCallback(() => {
    editRequestService.getEditAccess(journeyId)
      .then(setEditAccess)
      .catch(() => setEditAccess(null)); // fail open — never let this block the page
  }, [journeyId]);

  useEffect(() => { refreshEditAccess(); }, [refreshEditAccess]);

  const reEditLocked = !!editAccess && editAccess.enforced && !editAccess.editable;
  // ANDed with the existing delegation rule — complements it, never replaces it.
  const effectiveCanEdit = (section: InfoSection) => canEditSection(section) && !reEditLocked;

  const submitRaise = async () => {
    if (raiseReason.trim().length < 10) {
      toast.error('Please provide at least 10 characters describing why you need to edit.');
      return;
    }
    setRaising(true);
    try {
      const state = await editRequestService.raise(journeyId, raiseReason.trim());
      setEditAccess(state);
      setShowRaiseModal(false);
      setRaiseReason('');
      toast.success('Re-edit request sent to your service provider.');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Could not raise the request.');
    } finally {
      setRaising(false);
    }
  };

  // H-18 fix (2026-08-22): the Information tabs must fetch THIS journey's data, never a stale
  // scope's and never the identity profile by accident. The active-journey scope is set — or
  // CLEARED when the id-less /investor/journey route is used (deliberate primary-journey
  // fallback, instead of silently reusing whichever journey was open before) — inside the
  // load effect below, synchronously BEFORE the tab fetches fire. In SA proxy mode the proxy
  // adapter carries the journey, so the own-session scope is left untouched.

  // Loaded data states
  const [personalInfo, setPersonalInfo] = useState<UserPersonalInformationDto | null>(null);
  const [passport, setPassport] = useState<UserPassportDetailsDto | null>(null);
  const [experience, setExperience] = useState<InvestorExperienceDto | null>(null);
  const [_consents, setConsents] = useState<InvestorConsentsDto | null>(null);
  const [residential, setResidential] = useState<UserResidentialStatusDto | null>(null);
  const [taxInfo, setTaxInfo] = useState<UserTaxInfoDto | null>(null);
  const [bankDetails, setBankDetails] = useState<UserBankDetailsDto | null>(null);
  const [contactDetails, setContactDetails] = useState<UserContactDetailsDto | null>(null);
  const [nomination, setNomination] = useState<UserNominationDto | null>(null);
  const [_riskProfile, setRiskProfile] = useState<UserRiskProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const [dashboardData, setDashboardData] = useState<InvestorDashboardDto | null>(null);
  // §6.5 — "apply to your other journeys?" offer, shown after a successful save.
  const [propagationOffer, setPropagationOffer] = useState<PropagationOffer | null>(null);
  const [propagating, setPropagating] = useState(false);
  // §6.4 — a confirmed KYC document newer than this journey's fill is available.
  const [kycRefreshAvailable, setKycRefreshAvailable] = useState(false);
  const [applyingRefresh, setApplyingRefresh] = useState(false);
  // "Not now" is remembered per journey for the login session (user 2026-09-05: the offer
  // re-appeared on every refresh/revisit). Same sessionStorage pattern as the dashboard's
  // product-intro popup — it re-offers on the next login, not on every page load.
  const KYC_REFRESH_DISMISS_KEY = 'fs.kycRefreshDismissedJourneys';
  const kycRefreshDismissed = (id: string): boolean => {
    try {
      return (JSON.parse(sessionStorage.getItem(KYC_REFRESH_DISMISS_KEY) || '[]') as string[]).includes(id);
    } catch { return false; }
  };
  const dismissKycRefresh = (id: string): void => {
    try {
      const ids = new Set<string>(JSON.parse(sessionStorage.getItem(KYC_REFRESH_DISMISS_KEY) || '[]'));
      ids.add(id);
      sessionStorage.setItem(KYC_REFRESH_DISMISS_KEY, JSON.stringify(Array.from(ids)));
    } catch { /* storage unavailable — worst case the offer shows again */ }
  };

  // Master data
  const [taxResidencyCountries, setTaxResidencyCountries] = useState<MasterCountryDto[]>([]);
  const [isdCodes, setIsdCodes] = useState<IsdCodeValuesDto[]>([]);
  // Nationalities carry their ISO code, which is how Personal Info maps the locked
  // Citizenship to a Country of Birth (QA #10).
  const [nationalities, setNationalities] = useState<MasterLookupDto[]>([]);

  // Load master data
  useEffect(() => {
    contentService
      .getNationalities()
      .then((list) => setNationalities(list || []))
      .catch((e) => console.error('Failed to load nationalities:', e));
    contentService
      .getCountries()
      .then((list) =>
        setTaxResidencyCountries(
          [...list].sort((a, b) =>
            (a.ssName || '').localeCompare(b.ssName || '', undefined, { sensitivity: 'base' })
          )
        )
      )
      .catch(() => { });
  }, []);

  useEffect(() => {
    contentService
      .getIsdCodes()
      .then((list) =>
        setIsdCodes(
          [...list].sort((a, b) =>
            (a.countryName || '').localeCompare(b.countryName || '', undefined, { sensitivity: 'base' })
          )
        )
      )
      .catch(() => { });
  }, []);

  // C4: guards a rapid journey switch — an in-flight loadData for the OLD journey must not
  // apply its results after a newer load has started (stale data under the new scope).
  const loadSeqRef = useRef(0);
  // C4: which journey the states currently hold — a mismatch means a journey switch, which
  // clears + regates; a match (post-save refresh) updates in place.
  const lastLoadedJourneyRef = useRef<string | null | undefined>(undefined);

  // Load all form data
  const loadData = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      // H-18: scope FIRST, fetch second — profileService.journeyParams() reads this scope,
      // so every tab request below carries exactly this page's journeyId (or none, cleared).
      if (!isProxyMode) {
        setActiveInvestorJourney(journeyId ?? null);
      }
      // C4 fix (audit 2026-08-25): on a journey SWITCH the mounted tab forms kept the OLD
      // journey's values (no remount, hydrate skipped on null) — a Save could write journey
      // A's data under journey B, and an empty section on the new journey kept showing A's
      // data. On a switch only: gate the tab area behind loading and drop the stale states,
      // so the forms unmount and remount fresh against the new journey's data (null =
      // genuinely empty → blank form). Same-journey reloads (post-save refresh via
      // handleSectionSaved) keep the in-place update — no spinner flash, no remount.
      if (lastLoadedJourneyRef.current !== (journeyId ?? null)) {
        lastLoadedJourneyRef.current = journeyId ?? null;
        setLoading(true);
        setPersonalInfo(null);
        setPassport(null);
        setExperience(null);
        setConsents(null);
        setResidential(null);
        setTaxInfo(null);
        setBankDetails(null);
        setContactDetails(null);
        setNomination(null);
        setRiskProfile(null);
        setDashboardData(null);
      }
      const [info, pass, exp, cons, res, tax, bank, contact, nom, risk, dashboard] = await Promise.all([
        profileService.getPersonalInfo().catch(e => { console.error('Failed to load personal info:', e); return null; }),
        profileService.getPassport().catch(e => { console.error('Failed to load passport:', e); return null; }),
        profileService.getExperience().catch(e => { console.error('Failed to load experience:', e); return null; }),
        profileService.getConsents().catch(e => { console.error('Failed to load consents:', e); return null; }),
        profileService.getResidentialStatus().catch(e => { console.error('Failed to load residential:', e); return null; }),
        profileService.getTaxInfo().catch(e => { console.error('Failed to load tax info:', e); return null; }),
        profileService.getBankDetails().catch(e => { console.error('Failed to load bank details:', e); return null; }),
        profileService.getContactDetails().catch(e => { console.error('Failed to load contact details:', e); return null; }),
        profileService.getNomination().catch(e => { console.error('Failed to load nomination:', e); return null; }),
        profileService.getRiskProfile().catch(e => { console.error('Failed to load risk profile:', e); return null; }),
        // Scope the header + stepper to the opened journey (route :journeyId). SA proxy mode has no
        // route param → undefined → the proxy appends its own selectedJourneyId.
        investorService.getDashboard(journeyId).catch(e => { console.error('Failed to load dashboard:', e); return null; }),
      ]);

      if (seq !== loadSeqRef.current) {
        return; // a newer journey's load has started — discard this stale result set
      }
      setPersonalInfo(info ?? null);
      setPassport(pass ?? null);
      setExperience(exp ?? null);
      setConsents(cons ?? null);
      setResidential(res ?? null);
      setTaxInfo(tax ?? null);
      setBankDetails(bank ?? null);
      setContactDetails(contact ?? null);
      setNomination(nom ?? null);
      setRiskProfile(risk ?? null);
      setDashboardData(dashboard);
    } catch (err: any) {
      console.error('Error loading data:', err);
      toast.error('Failed to load investor information');
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, isProxyMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // §6.4 — offer (never auto-apply) when a newer confirmed KYC document exists.
  useEffect(() => {
    let cancelled = false;
    if (!journeyId) { setKycRefreshAvailable(false); return; }
    // Already declined this session — don't ask again until the next login.
    if (kycRefreshDismissed(journeyId)) { setKycRefreshAvailable(false); return; }
    investorService.isKycRefreshAvailable(journeyId)
      .then((available) => { if (!cancelled) setKycRefreshAvailable(available); })
      .catch(() => { if (!cancelled) setKycRefreshAvailable(false); });
    return () => { cancelled = true; };
  }, [journeyId]);

  // Shared context for all form sections
  // Laravel hides every tab's Submit button once the profile is finally
  // submitted, unless an admin has re-opened it for edits
  // (`final_submit_status == 1 && !$editAccessEnabled`). Journey-scoped
  // submissions report this via readiness; otherwise fall back to the
  // identity-level flag, matching FinalSubmissionForm.
  const [lockedAfterSubmit, setLockedAfterSubmit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    profileService
      .getFinalSubmitReadiness()
      .then((r) => {
        if (cancelled) return;
        // readiness.alreadySubmitted is authoritative for both cases now: the
        // journey's latest submission when one is scoped, and
        // user_personal_information.final_submit_status otherwise — the same
        // column Laravel gates its Submit buttons on.
        // Two ways a submitted journey reopens, and both must lift the lock: the SP sent the
        // submission back (`reeditable`), or the investor raised a re-edit request and the SP
        // approved it (`editUnlocked`). Only the first was read here, so an approval left every
        // section's Submit button hidden while the banner claimed editing was unlocked.
        setLockedAfterSubmit(!!r?.alreadySubmitted && !r?.reeditable && !r?.editUnlocked);
      })
      .catch(() => {
        // Can't tell → leave the tabs editable. Wrongly locking would strand
        // the investor; wrongly showing Submit is harmless.
        if (!cancelled) setLockedAfterSubmit(false);
      });
    return () => { cancelled = true; };
  }, [dashboardData]);

  // Tab -> backend section key + human label, used by both the prefilled hint and the
  // propagation prompt so the two can never disagree.
  const TAB_SECTIONS: Record<string, { key: string; label: string }> = {
    personal: { key: 'personalInfo', label: 'Personal Info' },
    bank: { key: 'bankDetails', label: 'Bank Details' },
    passport: { key: 'passport', label: 'Passport Details' },
    residential: { key: 'residential', label: 'Residential Status' },
    tax: { key: 'taxInfo', label: 'Tax Info' },
    contact: { key: 'contactDetails', label: 'Contact Details' },
    nomination: { key: 'nomination', label: 'Nomination' },
    other: { key: 'riskProfile', label: 'Other Info' },
  };

  /**
   * Every tab's onSave. Reloads as before, then offers to copy the section into the
   * investor's other journeys. A single-journey investor gets no targets back, so the
   * prompt never appears for them.
   */
  const handleSectionSaved = useCallback(async () => {
    await loadData();
    // Final Submit runs through here too: re-reading the lock state is what makes the
    // post-submission Re-Edit tab appear straight away instead of only after a reload.
    refreshEditAccess();
    // Auto-advance (user 2026-08-26): after a successful section save, move to the next
    // VISIBLE tab so the investor flows through the form without hunting for the next
    // step. Final Submit is the end of the line — no advance from there. The propagation
    // offer (below) renders as an overlay, so advancing underneath it is fine.
    // INVESTOR-TYPE-DRIVEN: the order is the canonical tab sequence filtered through the
    // same per-type visibility rules that render the tab row (profileVisibility.ts) — so
    // it skips exactly the tabs this investor type hides (today: Passport for RI), and a
    // future type ruleset hiding other sections is honoured automatically, no code change.
    // (Computed inline — the showPassportTab const is declared after this callback.)
    const investorTypeNow = dashboardData?.investor?.investorType ?? undefined;
    const TAB_SECTION_KEY: Record<string, ProfileSectionKey> = {
      personal: 'personal', bank: 'bank', passport: 'passport', residential: 'residential',
      tax: 'tax', contact: 'contact', nomination: 'nomination', other: 'risk', finalSubmit: 'finalSubmit',
    };
    const tabOrder = (['personal', 'bank', 'passport', 'residential', 'tax', 'contact', 'nomination', 'other', 'finalSubmit'] as TabType[])
      .filter((t) => isSectionVisible(investorTypeNow, TAB_SECTION_KEY[t]));
    const idx = tabOrder.indexOf(activeTab);
    if (idx >= 0 && idx < tabOrder.length - 1) {
      setActiveTab(tabOrder[idx + 1]);
    }
    const section = TAB_SECTIONS[activeTab]?.key;
    if (!section || !journeyId) return;
    try {
      const offer = await profileService.getPropagationTargets(section);
      if (offer && offer.targets.length > 0) setPropagationOffer(offer);
    } catch {
      // Offering is a convenience — never block or alarm on failure.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, activeTab, journeyId, refreshEditAccess, dashboardData]);

  const confirmPropagation = async (targetJourneyIds: string[]) => {
    if (!propagationOffer) return;
    setPropagating(true);
    try {
      const result = await profileService.propagateSection(propagationOffer.section, targetJourneyIds);
      toast.success(result.message || 'Applied to your other journeys');
      setPropagationOffer(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not apply to the other journeys');
    } finally {
      setPropagating(false);
    }
  };

  const applyKycRefresh = async () => {
    if (!journeyId) return;
    setApplyingRefresh(true);
    try {
      const result = await investorService.applyKycRefresh(journeyId);
      setKycRefreshAvailable(false);
      await loadData();
      toast.success(
        result?.conflicts?.length
          ? 'Updated. Some details differ from your documents — please review.'
          : 'This journey has been updated from your newer document.'
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update from the newer document');
    } finally {
      setApplyingRefresh(false);
    }
  };

  // H-15: latest OCR-imported value per field (journey-person-scoped). Empty = nothing locks.
  const [ocrImported, setOcrImported] = useState<Record<string, string>>({});
  useEffect(() => {
    kycDocumentsService.ocrImportedValues(journeyId)
      .then((v) => setOcrImported(v || {}))
      .catch(() => setOcrImported({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const isOcrLocked = (targetField: string, currentValue?: string | null): boolean => {
    const imported = ocrImported[targetField];
    if (!imported || currentValue == null || String(currentValue).trim() === '') return false;
    return imported.trim().toLowerCase() === String(currentValue).trim().toLowerCase();
  };

  const sharedContext: SharedFormContext = {
    taxResidencyCountries,
    nationalities,
    isdCodes,
    dashboardData,
    // Already loaded for the Personal Info tab — shared so sibling tabs (RI PAN
    // details on Tax Info) can prefill without an extra fetch.
    personalInfo,
    reloadData: loadData,
    lockedAfterSubmit,
    isOcrLocked,
  };

  // Investor type drives the per-type profile visibility rules (hide Passport
  // tab for RI, rename Residential Status → Aadhaar Details, hide individual
  // fields). Defaults to undefined → "show everything", so NRI/OCI/etc are
  // unaffected.
  const investorType = dashboardData?.investor?.investorType ?? undefined;
  const showPassportTab = isSectionVisible(investorType, 'passport');
  const residentialTabLabel = getSectionLabel(investorType, 'residential', 'Residential Status');

  // A green tick on each completed profile tab. Completion comes from the same source as the
  // Final-Submit gate (investor_information_status flags → dashboard progress.sections).
  // (Laravel's own Information tabs carry no ticks — this strip is ours.)
  const sections = dashboardData?.progress?.sections;
  const sectionDone = (key: string): boolean => !!sections?.[key]?.completed;
  // The Final Submit tick means SUBMITTED, not "ready to submit". It used to key off an
  // all-sections-complete check, but Other Info is the LAST gate section — so saving it flipped
  // the tick on immediately, and the same green check meant "section filled" on eight tabs and
  // "you may now proceed" on the ninth. finalSubmitted is journey-scoped, so each journey ticks
  // on its own submission. Display only; the real gate is re-evaluated server-side in finalSubmit.
  const finalSubmitDone = !!editAccess?.finalSubmitted;
  // Account opened = the journey is complete, so re-editing is over: the tab goes away and the
  // request cannot be raised (user 2026-09-10). The server enforces the same rule in
  // InvestorEditRequestService.raise, so hiding here is convenience, not the guarantee.
  const accountOpened = !!editAccess?.accountOpened;
  const reEditAvailable = finalSubmitDone && !accountOpened;
  // The Re-Edit tab only exists on a submitted journey. Switching to a journey that has not
  // been submitted would otherwise leave it selected with nothing rendered.
  useEffect(() => {
    // Also covers standing on the tab when the account opens, and switching to a journey whose
    // account is already open — either way the tab is gone and this must not be left selected.
    if (activeTab === 'reEdit' && !reEditAvailable) {
      setActiveTab('finalSubmit');
    }
  }, [activeTab, reEditAvailable]);
  const TabTick = () => (
    <i className="bi bi-check-circle-fill" style={{ color: '#10b981', marginLeft: 6, fontSize: 12 }} />
  );

  // Product / plan shown under the Service Provider line in the journey header.
  const productAssignment = dashboardData?.productAssignment;
  const planLabel =
    productAssignment?.planName &&
    !['not applicable', 'na', '-', ''].includes(productAssignment.planName.trim().toLowerCase())
      ? productAssignment.planName
      : null;

  if (loading) {
    return (
      <div className="facilon-dashboard-wrapper">
        <main className="container-fluid dashboard-container-main">
          <div className="loading-container" style={{ textAlign: 'center', padding: '100px 0' }}>
            <div className="spinner"></div>
            <p>Loading investor information...</p>
          </div>
        </main>
      </div>
    );
  }

  // SOW gate: the onboarding journey can't be opened until the investor has agreed to their
  // Statement of Work. A revoke re-closes it. (undefined = backend not reporting → allow.)
  // The SOW is the investor's personal/legal act — in SA proxy mode show a waiting notice
  // instead of the Consent Centre button (an agent must never agree on the investor's behalf).
  if (dashboardData?.sowAgreed === false) {
    return (
      <div className="facilon-dashboard-wrapper">
        <main className="container-fluid dashboard-container-main">
          <div className="alert alert-warning mt-4" role="alert" style={{ maxWidth: 640, margin: '60px auto' }}>
            <h5 className="mb-2">Statement of Work required</h5>
            {delegationPerms.isProxyMode ? (
              <p className="mb-0">
                Waiting for the investor to accept their Statement of Work. The onboarding forms
                unlock for both of you once they agree in their Consent Centre.
              </p>
            ) : (
              <>
                <p className="mb-3">
                  You need to review and agree to your Statement of Work before you can continue your
                  onboarding journey. If you previously revoked it, please agree again to proceed.
                </p>
                <button className="btn btn-primary" onClick={() => navigate('/investor/consents')}>
                  Go to Consent Centre
                </button>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="facilon-dashboard-wrapper">
      <main className="container-fluid dashboard-container-main p-0">
        <div className="investor-profile">

          {delegationPerms.isProxyMode && !delegationPerms.canViewProfile && (
            <div className="alert alert-danger" role="alert" style={{ margin: '1rem 0' }}>
              <strong>Access Restricted:</strong> You don't have permission to view this investor's profile.
              Please ask the investor to update your delegation permissions to include "View Profile" access.
            </div>
          )}

          {delegationPerms.isProxyMode && delegationPerms.canViewProfile && !canEditSection('KYC') && (
            <div className="alert alert-warning" role="alert" style={{ margin: '1rem 0' }}>
              <strong>Read-Only Mode:</strong> Your access scope
              ({SCOPE_LABELS[delegationPerms.scope as DelegationScope] ?? delegationPerms.scope}) lets you view
              information but not change it. The investor can change the scope in their Permissions Center.
            </div>
          )}

          {delegationPerms.isProxyMode && delegationPerms.canViewProfile
            && canEditSection('KYC') && !canEditSection('ONBOARDING') && (
            <div className="alert alert-warning" role="alert" style={{ margin: '1rem 0' }}>
              <strong>{SCOPE_LABELS[delegationPerms.scope as DelegationScope] ?? delegationPerms.scope}:</strong> You
              can edit Personal, Contact, Passport, Residential and Tax. Bank, Nomination and Other Info are
              read-only under this scope. The investor can change the scope in their Permissions Center.
            </div>
          )}

          {showRaiseModal && (
            <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40" onClick={() => setShowRaiseModal(false)}>
              <div className="bg-white rounded-xl border border-gray-200 max-w-[480px] w-[90%] shadow-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center p-3 border-b border-gray-200">
                  <h2 className="m-0 text-[14px] font-bold text-slate-800">Request Re-Edit</h2>
                  <button className="text-[20px] text-slate-400 bg-transparent border-0 cursor-pointer p-0 leading-none" onClick={() => setShowRaiseModal(false)}>×</button>
                </div>
                <div className="p-3">
                  <p className="text-[12px] text-slate-600 mt-0">
                    Describe why you need to change your submitted information. Your service provider
                    will review and approve or decline the request.
                  </p>
                  <textarea
                    value={raiseReason}
                    onChange={(e) => setRaiseReason(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    placeholder="Reason (minimum 10 characters)"
                    className="w-full border border-slate-300 rounded-md p-2 text-[13px]"
                  />
                </div>
                <div className="p-3 border-t border-gray-200 flex justify-end gap-2">
                  <button type="button" className="text-[11px] font-bold h-[30px] px-3 rounded-md border border-slate-300 bg-white cursor-pointer" onClick={() => setShowRaiseModal(false)}>Cancel</button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[11px] font-bold h-[30px] px-3 rounded-md border-0 cursor-pointer disabled:opacity-50"
                    disabled={raising}
                    onClick={submitRaise}
                  >
                    {raising ? 'Sending…' : 'Send Request'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Merged Header Banner with Stepper */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4 shadow-sm">
            <div className="p-3 flex flex-col md:flex-row justify-between items-center text-white gap-3 md:gap-6" style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}>
              <div className="flex-shrink-0">
                <h2 className="m-0 text-base font-bold text-white tracking-tight">Investor Information</h2>
                {productAssignment?.serviceProviderName && (
                  <p className="m-0 text-[11.5px] font-normal text-white/80 mt-0.5">
                    Service Provider: <strong>{productAssignment.serviceProviderName}</strong>
                  </p>
                )}
                {productAssignment?.productName && (
                  <p className="m-0 text-[11.5px] font-normal text-white/80 mt-0.5">
                    Product: <strong>{productAssignment.productName}</strong>
                    {planLabel && <> · Plan: <strong>{planLabel}</strong></>}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                <PremiumJourneyStepper dashboardData={dashboardData} compact={true} />
              </div>
            </div>
          </div>

          <div className="investor-profile__tabs">
            <button
              type="button"
              className={activeTab === 'personal' ? 'active' : ''}
              onClick={() => setActiveTab('personal')}
            >
              Personal Info
              {sectionDone('personalInfo') && <TabTick />}
            </button>
            <button
              type="button"
              className={activeTab === 'bank' ? 'active' : ''}
              onClick={() => setActiveTab('bank')}
            >
              Bank Details
              {sectionDone('bankDetails') && <TabTick />}
            </button>
            {showPassportTab && (
              <button
                type="button"
                className={activeTab === 'passport' ? 'active' : ''}
                onClick={() => setActiveTab('passport')}
              >
                Passport
                {sectionDone('passport') && <TabTick />}
              </button>
            )}
            <button
              type="button"
              className={activeTab === 'residential' ? 'active' : ''}
              onClick={() => setActiveTab('residential')}
            >
              {residentialTabLabel}
              {sectionDone('residential') && <TabTick />}
            </button>
            <button
              type="button"
              className={activeTab === 'tax' ? 'active' : ''}
              onClick={() => setActiveTab('tax')}
            >
              Tax Info
              {sectionDone('taxInfo') && <TabTick />}
            </button>
            <button
              type="button"
              className={activeTab === 'contact' ? 'active' : ''}
              onClick={() => setActiveTab('contact')}
            >
              Contact Details
              {sectionDone('contactDetails') && <TabTick />}
            </button>
            <button
              type="button"
              className={activeTab === 'nomination' ? 'active' : ''}
              onClick={() => setActiveTab('nomination')}
            >
              Nomination
              {sectionDone('nomination') && <TabTick />}
            </button>
            <button
              type="button"
              className={activeTab === 'other' ? 'active' : ''}
              onClick={() => setActiveTab('other')}
            >
              Other Info
              {sectionDone('riskProfile') && <TabTick />}
            </button>
            <button
              type="button"
              className={activeTab === 'finalSubmit' ? 'active' : ''}
              onClick={() => setActiveTab('finalSubmit')}
            >
              {/* UAT 23: an agent with Onboarding Only access asks the investor to submit — the
                  tab is "Submit for Approval" and carries no completion tick (submitting is
                  the investor's act, so the agent's tab is never "done"). */}
              {delegationPerms.isProxyMode && delegationPerms.scope === 'ONBOARDING_ONLY'
                ? 'Submit for Approval'
                : 'Final Submit'}
              {finalSubmitDone && !(delegationPerms.isProxyMode && delegationPerms.scope === 'ONBOARDING_ONLY') && <TabTick />}
            </button>
            {/* Post-submission tab — journey-scoped, so it appears on a submitted journey only
                and never on the investor's other journeys. */}
            {reEditAvailable && (
              <button
                type="button"
                className={activeTab === 'reEdit' ? 'active' : ''}
                onClick={() => setActiveTab('reEdit')}
              >
                Re-Edit
              </button>
            )}
          </div>

          {/* §6.4 — a newer confirmed KYC document exists for this journey. Offer only:
              nothing is applied until the investor asks for it, and the backend refuses
              outright once the journey is submitted. Shown as a POPUP when the journey
              opens (user 2026-08-25), not as an inline banner above the forms. */}
          <AlertDialog
            show={kycRefreshAvailable}
            title="Newer KYC document available"
            message="You have uploaded a newer KYC document since this journey was filled in. Would you like to update this journey with it?"
            confirmText={applyingRefresh ? 'Updating…' : 'Update this journey'}
            cancelText="Not now"
            onConfirm={applyKycRefresh}
            onClose={() => {
              if (applyingRefresh) return;
              // Remember the decline for this session — the offer was re-appearing on
              // every refresh/revisit (user 2026-09-05).
              if (journeyId) dismissKycRefresh(journeyId);
              setKycRefreshAvailable(false);
            }}
          />

          {activeTab === 'personal' && (
            <PersonalInformationForm
              initialData={personalInfo}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('KYC')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'bank' && (
            <BankDetailsForm
              initialData={bankDetails}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('ONBOARDING')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'passport' && showPassportTab && (
            <PassportDetailsForm
              initialData={passport}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('KYC')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'residential' && (
            <ResidentialStatusForm
              initialData={residential}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('KYC')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'tax' && (
            <TaxInformationForm
              initialData={taxInfo}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('KYC')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'contact' && (
            <ContactDetailsForm
              initialData={contactDetails}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('KYC')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'nomination' && (
            <NominationForm
              initialData={nomination}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('ONBOARDING')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {activeTab === 'other' && (
            <RiskProfileForm
              initialData={experience}
              onSave={handleSectionSaved}
              canEdit={effectiveCanEdit('ONBOARDING')}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {propagationOffer && (
            <PropagationPrompt
              offer={propagationOffer}
              sectionLabel={TAB_SECTIONS[activeTab]?.label ?? 'these details'}
              saving={propagating}
              onConfirm={confirmPropagation}
              onDismiss={() => setPropagationOffer(null)}
            />
          )}

          {activeTab === 'finalSubmit' && (
            <FinalSubmissionForm
              onSave={handleSectionSaved}
              canSubmit={canSubmit}
              isProxyMode={isProxyMode}
              sharedContext={sharedContext}
            />
          )}

          {/* Post-submission confirmation + re-edit request, for THIS journey only. */}
          {activeTab === 'reEdit' && reEditAvailable && editAccess && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="m-0 text-[15px] font-bold text-slate-800">
                Information Submitted{reEditLocked ? ' and Locked' : ''}
              </h3>
              <p className="text-[12.5px] text-slate-600 mt-1 mb-3">
                Your information for
                {productAssignment?.productName ? <> <strong>{productAssignment.productName}</strong></> : ' this journey'}
                {productAssignment?.serviceProviderName && <> with <strong>{productAssignment.serviceProviderName}</strong></>}
                {' '}has been submitted.
              </p>

              {editAccess.requestStatus === 'APPROVED' ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-slate-700">
                  <strong>Editing unlocked.</strong> Your re-edit request was approved. Make your changes
                  in the tabs above and submit again — your information locks after re-submission.
                </div>
              ) : editAccess.requestStatus === 'PENDING' ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-slate-700">
                  <strong>Re-edit request pending.</strong> Waiting for your service provider to approve
                  or decline. You will be e-mailed as soon as they decide.
                </div>
              ) : (
                <>
                  {editAccess.requestStatus === 'REJECTED' && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12.5px] text-slate-700 mb-3">
                      <strong>Your previous re-edit request was declined.</strong>
                      {editAccess.rejectionRemarks ? <> — “{editAccess.rejectionRemarks}”</> : null}
                    </div>
                  )}
                  <p className="text-[12.5px] text-slate-600 mt-0 mb-2">
                    Need to change something? Ask your service provider to reopen this journey.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowRaiseModal(true)}
                    disabled={!editAccess.canRaise || (delegationPerms.isProxyMode && !delegationPerms.canSubmitForms)}
                    title={delegationPerms.isProxyMode && !delegationPerms.canSubmitForms
                      ? "You don't have permission to request re-edit on behalf of this investor."
                      : undefined}
                    className="inline-flex items-center justify-center bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[11px] font-bold h-[30px] px-3 rounded-md border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Request Re-Edit
                  </button>
                  {delegationPerms.isProxyMode && !delegationPerms.canSubmitForms && (
                    <p className="text-[11px] text-amber-600 mt-2 mb-0">
                      Requesting re-edit needs the Submit Forms permission. Contact the investor to update
                      delegation permissions.
                    </p>
                  )}
                </>
              )}

              {!!editAccess.cycleCount && editAccess.cycleCount > 0 && (
                <p className="text-[11px] text-slate-400 mt-3 mb-0">
                  Re-edit cycles used for this journey: {editAccess.cycleCount}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
